import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { FillColorStop, FillValue } from '@company-box/models';
import { hexToCmyk } from '@company-box/utils';
import { ColorPickerChange, ColorPickerComponent } from '../color-picker/color-picker.component';

// Solid/linear/radial fill picker - wraps two ColorPickerComponent instances (one per gradient
// stop) rather than adding a gradient mode into ColorPickerComponent itself, so brush/text/pattern
// colors (which stay plain hex+CMYK, no gradient concept) are untouched. See
// docs/plans/design3d-gradient-fills-plan.md.
//
// Emits FillValue - the recipe, never a live Fabric Gradient - callers are responsible for baking
// linear/radial values to an image (gradient.util.ts's svgGradient()) before it reaches a Fabric
// object's own `fill`; see that plan's correction note for why.
@Component({
  standalone: true,
  selector: 'app-gradient-picker',
  templateUrl: './gradient-picker.component.html',
  styleUrls: ['./gradient-picker.component.scss'],
  imports: [FormsModule, PopoverModule, MatSliderModule, MatTooltipModule, ColorPickerComponent],
})
export class GradientPickerComponent {
  @Input() value: FillValue = { type: 'solid', hex: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 100 } };
  @Input() ariaLabel = 'Choose fill';
  // Hides the color swatch(es) - Color in solid mode, From/To in linear/radial mode. The Fill tool
  // (Draw & Fill) sets this false to drop them from its own picker (its gradients are always fixed
  // white/black, see currentFromStop/currentToStop below - editable afterward via Shape Settings or
  // the on-canvas drag handles once the stamp is placed); Shape Settings/Background Settings leave
  // it at the default (shown).
  @Input() showColorFields = true;
  // Hides the Angle slider in linear mode - same Fill tool use case as showColorFields above (its
  // angle stays 0 until the stamp is placed, then it's editable like any other shape).
  @Input() showAngle = true;

  @Output() valueChange = new EventEmitter<FillValue>();

  // Drives .gradient-picker-thumb's slide position - only fedex-ui shows the thumb (styles-fedex.scss);
  // same sliding fill as gradient-picker-redesign.component.ts's own activeIndex.
  get activeIndex(): number {
    return this.value.type === 'solid' ? 0 : this.value.type === 'linear' ? 1 : 2;
  }

  // Fixed white/black rather than carrying over the current solid color - switching to a gradient
  // always starts from the same two stops, regardless of what solid color was picked before.
  private get currentFromStop(): FillColorStop {
    return this.value.type === 'solid' ? { hex: '#ffffff', cmyk: hexToCmyk('#ffffff') } : this.value.from;
  }

  private get currentToStop(): FillColorStop {
    return this.value.type === 'solid' ? { hex: '#000000', cmyk: hexToCmyk('#000000') } : this.value.to;
  }

  setMode(mode: FillValue['type']): void {
    if (this.value.type === mode) {
      return;
    }
    if (mode === 'solid') {
      const from = this.currentFromStop;
      this.emit({ type: 'solid', hex: from.hex, cmyk: from.cmyk });
      return;
    }
    const from = this.currentFromStop;
    const to = this.currentToStop;
    if (mode === 'linear') {
      this.emit({ type: 'linear', from, to, angle: this.value.type === 'linear' ? this.value.angle : 0 });
    } else {
      this.emit({
        type: 'radial',
        from,
        to,
        center: this.value.type === 'radial' ? this.value.center : { x: 0.5, y: 0.5 },
        radius: this.value.type === 'radial' ? this.value.radius : 0.5,
      });
    }
  }

  setSolidColor(color: ColorPickerChange): void {
    this.emit({ type: 'solid', hex: color.hex, cmyk: color.cmyk });
  }

  setFromColor(color: ColorPickerChange): void {
    if (this.value.type === 'solid') {
      return;
    }
    this.emit({ ...this.value, from: color });
  }

  setToColor(color: ColorPickerChange): void {
    if (this.value.type === 'solid') {
      return;
    }
    this.emit({ ...this.value, to: color });
  }

  setAngle(angle: number): void {
    if (this.value.type !== 'linear') {
      return;
    }
    this.emit({ ...this.value, angle });
  }

  blurSliderThumb(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  private emit(value: FillValue): void {
    this.value = value;
    this.valueChange.emit(value);
  }
}
